import { motion } from "framer-motion";
import { Hand, Palette, Puzzle, Hammer, BookOpen, Leaf, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const CLAY_IMG =
  "https://images.unsplash.com/photo-1673252413416-16fbc2c8d74a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwyfHxjaGlsZHJlbiUyMGNyYWZ0JTIwY2xheXxlbnwwfHx8fDE3ODc4NTY2NzJ8MA&ixlib=rb-4.1.0&q=85";

const STORY_IMG = "/images/tree-corner.jpg";

const activities = [
  { icon: Hand, title: "Clay & Ceramics", body: "Pinch pots, tiny creatures, and the patience of slow drying.", span: "md:col-span-4", tint: "text-terracotta" },
  { icon: Palette, title: "Fiber & Craft", body: "Weaving, stitching and dyeing with wool, thread and cloth.", span: "md:col-span-4", tint: "text-sage" },
  { icon: Puzzle, title: "Board Games & Puzzles", body: "Strategy games, jigsaws and quiet battles of wit around the big table.", span: "md:col-span-4", tint: "text-plum" },
  { icon: Hammer, title: "Woodworking", body: "Sanding, joining and simple builds with real tools, safely guided.", span: "md:col-span-5", tint: "text-terracotta" },
  { icon: Leaf, title: "Nature Discovery", body: "Outdoor walks, seed planting and field-guide sketching.", span: "md:col-span-7", tint: "text-sage" },
];

export const Activities = () => (
  <section
    id="activities"
    data-testid="activities-section"
    className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40"
  >
    <Reveal>
      <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-plum">
        03 · Hands-On Studios
      </p>
      <div className="flex flex-wrap items-end justify-between gap-8">
        <h2 className="max-w-2xl font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
          Where busy hands <em className="text-plum">quiet busy minds.</em>
        </h2>
        <p className="max-w-sm text-base leading-relaxed text-slate2">
          Six open studios run all week — drop in after school, stay for the
          afternoon. Everything is included with a visit.
        </p>
      </div>
    </Reveal>

    <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12">
      <Reveal className="md:col-span-7" delay={0.05}>
        <div
          data-testid="activity-storytelling-card"
          className="group relative h-full min-h-[320px] overflow-hidden rounded-3xl"
        >
          <img
            src={STORY_IMG}
            alt="Children reading together under the library's handmade story tree"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <BookOpen size={22} className="mb-3 text-cream/90" />
            <h3 className="font-serif text-3xl text-cream">Storytelling Circles</h3>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-cream/80">
              Read-aloud sessions every evening — voices, puppets, and endings
              voted on by the audience.
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal className="md:col-span-5" delay={0.12}>
        <div
          data-testid="activity-clay-image-card"
          className="group relative h-full min-h-[320px] overflow-hidden rounded-3xl"
        >
          <img
            src={CLAY_IMG}
            alt="Children shaping clay with their hands"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-plum/80 via-plum/10 to-transparent" />
          <div className="absolute bottom-0 p-8">
            <p className="font-serif text-xl italic leading-snug text-cream">
              "The clay table is where shy children speak first."
            </p>
          </div>
        </div>
      </Reveal>

      {activities.map((a, i) => (
        <Reveal key={a.title} className={a.span} delay={0.06 * i}>
          <motion.div
            data-testid={`activity-card-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            whileHover={{ y: -6 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="flex h-full flex-col justify-between rounded-3xl border border-ink/10 bg-cream p-8"
          >
            <a.icon size={26} strokeWidth={1.6} className={a.tint} />
            <div className="mt-10">
              <h3 className="font-serif text-2xl text-ink">{a.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate2">{a.body}</p>
            </div>
          </motion.div>
        </Reveal>
      ))}
    </div>
  </section>
);
